import { useQuery } from "@tanstack/react-query";
import { createContext, useContext } from "react";

import { ApiClientProvider } from "@/api/ApiClientProvider";
import type { AppBrandingDto } from "@/api/types";
import { ConnectionFailurePage } from "@/components/Error/ConnectionFailurePage";
import { DEFAULT_APP_BRANDING } from "@/helpers/app-branding";
import { useQueryParams } from "@/hooks/useQueryParam";
import { useConfigurationQueries } from "@/queries/configurations/queryOptions";

import { DocumentBranding } from "./DocumentBranding";
import { StartupLoading } from "./StartupLoading";

type PublicAppBrandingContextValues = {
  appBranding: AppBrandingDto;
};

const PublicAppBrandingContext = createContext<PublicAppBrandingContextValues | null>(null);

export function usePublicAppBranding(): PublicAppBrandingContextValues {
  const context = useContext(PublicAppBrandingContext);

  if (!context) {
    throw new Error("usePublicAppBranding must be used within an PublicAppBrandingProvider");
  }

  return context;
}

type PublicAppBrandingProviderProps = {
  children: React.ReactNode;
};

/** Resolves public branding before rendering its scoped API and UI. Catalog failures are terminal UI states. */
export function PublicAppBrandingProvider({ children }: PublicAppBrandingProviderProps): React.ReactNode {
  const [queryParams] = useQueryParams<{
    brand?: string;
  }>();

  const configurationQueries = useConfigurationQueries();
  const {
    data: listAppBrandingConfigs = [],
    isPending: isPendingListAppBrandingConfigs,
    isError: isErrorListAppBrandingConfigs,
  } = useQuery(configurationQueries.listAppBrandings());

  const appAliasMap = listAppBrandingConfigs.reduce<Record<string, AppBrandingDto>>((acc, curr) => {
    const currAliases = curr.aliases || [];
    currAliases.forEach((alias) => {
      acc[alias] = curr.id;
    });

    return acc;
  }, {});
  const supportedAppBrandings = listAppBrandingConfigs.map((config) => config.id);
  let appBranding: AppBrandingDto = DEFAULT_APP_BRANDING;
  if (queryParams.brand) {
    if (queryParams.brand in appAliasMap) {
      appBranding = appAliasMap[queryParams.brand];
    } else if (supportedAppBrandings.length > 0 && !supportedAppBrandings.includes(queryParams.brand as any)) {
      appBranding = DEFAULT_APP_BRANDING;
    } else {
      appBranding = queryParams.brand as AppBrandingDto;
    }
  }

  if (isPendingListAppBrandingConfigs) {
    return <StartupLoading />;
  }

  if (isErrorListAppBrandingConfigs) {
    return <ConnectionFailurePage />;
  }

  return (
    <PublicAppBrandingContext value={{ appBranding }}>
      <DocumentBranding brand={appBranding} />
      <ApiClientProvider appBranding={appBranding}>{children}</ApiClientProvider>
    </PublicAppBrandingContext>
  );
}
